import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import { DeleteCascadePayload, RelatedGroup } from '../api/cascade';
import { ApiError } from '../api/request';

interface Props {
    open: boolean;
    /** 弹窗标题, 如 "删除材料" */
    title: string;
    /** 被删主体的展示名, 如 "AB010-01 亚克力板" */
    targetLabel: React.ReactNode;
    /** 拉关联数据分组; 不传 = 无关联数据的普通删除确认 */
    loadPreview?: () => Promise<RelatedGroup[]>;
    /** 额外提醒 (琥珀色警示条), 用于"删了会影响别处"这类无需勾选的告知 */
    warning?: React.ReactNode;
    /** 确认回调: cascade = 勾选的组 key; related = 预检快照 (进审批工单展示) */
    onConfirm: (payload: DeleteCascadePayload) => Promise<void>;
    onClose: () => void;
}

/**
 * 通用删除确认弹窗 (2026-08-21 硬删语义配套):
 * 展示 delete-preview 的关联数据分组 —— 必删组勾选锁死, 可选组由用户决定是否连带删除,
 * 未勾选的按"解绑保留"处理 (说明文案来自后端 uncheckedNote). 勾选结果随 DELETE 请求体
 * 进审批工单, 审批人看到的即提交人勾选时的影响面.
 */
function DeleteCascadeModal({
    open,
    title,
    targetLabel,
    loadPreview,
    warning,
    onConfirm,
    onClose,
}: Props) {
    const [groups, setGroups] = useState<RelatedGroup[]>([]);
    const [checked, setChecked] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setGroups([]);
        setChecked(new Set());
        setError('');
        if (!loadPreview) return;
        setLoading(true);
        loadPreview()
            .then(setGroups)
            .catch((e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)))
            .finally(() => setLoading(false));
        // loadPreview 由调用方按目标构造, open 变化即目标变化, 不依赖函数引用稳定
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const optional = useMemo(() => groups.filter((g) => !g.required && !g.blocking), [groups]);
    // 必删组全部列出 (含 count=0): 删前要让人看清影响面的完整轮廓, 而不是只看有数据的几项
    const required = useMemo(() => groups.filter((g) => g.required && !g.blocking), [groups]);
    // 阻塞组只列有数据的 (没数据就不是阻塞); 任一存在则禁用确认
    const blockers = useMemo(() => groups.filter((g) => g.blocking && g.count > 0), [groups]);
    const blocked = blockers.length > 0;

    const toggle = (key: string) => {
        const next = new Set(checked);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setChecked(next);
    };

    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            await onConfirm({ cascade: Array.from(checked), related: groups });
            onClose();
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title={title} width={560}>
            <div className="space-y-4">
                <div className="text-sm">
                    确认删除 <span className="font-semibold">{targetLabel}</span>?
                    <span className="text-rose-600"> 删除为物理删除, 不可恢复</span>
                    。提交后生成审批工单, 审批通过后按下方勾选执行。
                </div>

                {warning && (
                    <div className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-800 p-2.5">
                        ⚠ {warning}
                    </div>
                )}

                {loading && <div className="text-xs text-slate-400">正在检查关联数据…</div>}

                {blocked && (
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-rose-700">⛔ 存在阻塞项, 解除后才能删除:</div>
                        <div className="rounded-lg border border-rose-200 bg-rose-50/60 divide-y divide-rose-200/70">
                            {blockers.map((g) => (
                                <div key={g.key} className="px-2.5 py-2 text-xs text-rose-800">
                                    <span className="font-medium">{g.label}</span>
                                    <span className="ml-1 font-mono">× {g.count}</span>
                                    {g.note && <span className="block text-rose-600/80 mt-0.5">{g.note}</span>}
                                    {(g.items || []).length > 0 && (
                                        <span className="mt-1 block space-y-0.5 rounded bg-white/70 px-2 py-1.5 font-mono text-[11px] leading-relaxed text-rose-700">
                                            {g.items!.map((it) => (
                                                <span key={it} className="block">
                                                    {it}
                                                </span>
                                            ))}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {optional.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-slate-600">
                            关联数据 · 勾选则一起删除, 不勾选按说明保留:
                        </div>
                        {optional.map((g) => (
                            <label
                                key={g.key}
                                className={`flex items-start gap-2 rounded-lg border p-2.5 text-xs ${
                                    g.count === 0
                                        ? 'border-slate-100 text-slate-300'
                                        : checked.has(g.key)
                                          ? 'border-rose-200 bg-rose-50/60'
                                          : 'border-slate-200 hover:bg-slate-50 cursor-pointer'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    disabled={g.count === 0 || busy}
                                    checked={checked.has(g.key)}
                                    onChange={() => toggle(g.key)}
                                />
                                <span>
                                    <span className="font-medium">{g.label}</span>
                                    <span className="ml-1 font-mono">× {g.count}</span>
                                    {g.note && (
                                        <span className="block text-slate-400 mt-0.5">{g.note}</span>
                                    )}
                                    {(g.items || []).length > 0 && (
                                        <span className="mt-1 block space-y-0.5 rounded bg-slate-50 px-2 py-1.5 font-mono text-[11px] leading-relaxed text-slate-500">
                                            {g.items!.map((it) => (
                                                <span key={it} className="block">
                                                    {it}
                                                </span>
                                            ))}
                                        </span>
                                    )}
                                    {g.count > 0 && g.uncheckedNote && !checked.has(g.key) && (
                                        <span className="block text-slate-400 mt-0.5">
                                            不勾选 → {g.uncheckedNote}
                                        </span>
                                    )}
                                    {g.count > 0 && checked.has(g.key) && (
                                        <span className="block text-rose-500 mt-0.5">
                                            {g.checkedNote || '将随主体一起删除'}
                                        </span>
                                    )}
                                </span>
                            </label>
                        ))}
                    </div>
                )}

                {required.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-slate-600">
                            从属数据 · 随主体一并删除 (不可取消):
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50/60 divide-y divide-slate-200/70">
                            {required.map((g) => (
                                <div
                                    key={g.key}
                                    className={`flex items-start gap-2 px-2.5 py-2 text-xs ${
                                        g.count === 0 ? 'text-slate-300' : 'text-slate-600'
                                    }`}
                                >
                                    <span className="mt-px">{g.count > 0 ? '✕' : '—'}</span>
                                    <span>
                                        <span className="font-medium">{g.label}</span>
                                        <span className="ml-1 font-mono">× {g.count}</span>
                                        {g.note && (
                                            <span className="block text-slate-400 mt-0.5">
                                                {g.note}
                                            </span>
                                        )}
                                        {(g.items || []).length > 0 && (
                                            <span className="mt-1 block space-y-0.5 rounded bg-white px-2 py-1.5 font-mono text-[11px] leading-relaxed text-slate-500">
                                                {g.items!.map((it) => (
                                                    <span key={it} className="block">
                                                        {it}
                                                    </span>
                                                ))}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={submit}
                        disabled={busy || loading || blocked}
                        title={blocked ? '存在阻塞项, 解除后才能删除' : undefined}
                    >
                        {busy ? '删除中…' : '确认删除'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default DeleteCascadeModal;
